import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Calendar,
  Users,
  FolderOpen,
  User,
  Clock,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  Eye
} from 'lucide-react';
import { userAPI, timesheetAPI, projectAPI } from '../services/api';
import { format, parseISO, isSameDay, startOfDay, endOfDay } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';

const AdminDraftReport = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [expandedManagers, setExpandedManagers] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch projects assigned to current admin (if not SUPER_ADMIN)
  const { data: assignedProjects = [], isLoading: projectsLoading } = useQuery(
    'admin-assigned-projects',
    () => projectAPI.getAssignedProjects(),
    {
      enabled: !!user && user?.role !== 'SUPER_ADMIN',
      select: (response) => response?.data?.projects || response?.data || [],
    }
  );

  // Get assigned project IDs for filtering
  const assignedProjectIds = useMemo(() => {
    if (user?.role === 'SUPER_ADMIN') {
      return null; // Super Admin sees all projects
    }
    return new Set(assignedProjects.map(p => p.id));
  }, [assignedProjects, user?.role]);

  // Fetch all employees and managers
  const { data: allUsers = [], isLoading: usersLoading } = useQuery(
    'all-users-for-draft-report',
    () => {
      if (user?.role === 'SUPER_ADMIN') {
        return userAPI.getAllEmployees().then(res => {
          const employees = res?.data?.data || res?.data || [];
          return userAPI.getAllManagers().then(managersRes => {
            const managers = managersRes?.data?.data || managersRes?.data || [];
            return [...employees, ...managers];
          });
        });
      } else {
        return userAPI.getManagersAndEmployeesAdmin().then(res => {
          return res?.data?.data || res?.data || [];
        });
      }
    },
    {
      enabled: !!user,
      select: (data) => data || [],
    }
  );

  // Fetch timesheets for all users
  const { data: allDraftTimesheets = [], isLoading: timesheetsLoading } = useQuery(
    ['draft-timesheets', allUsers, selectedDate, assignedProjectIds],
    async () => {
      if (!allUsers || allUsers.length === 0) return [];
      
      const selectedDateObj = parseISO(selectedDate);
      const startOfSelectedDay = startOfDay(selectedDateObj);
      const endOfSelectedDay = endOfDay(selectedDateObj);
      
      // Fetch timesheets for each user
      const timesheetPromises = allUsers.map(async (userItem) => {
        try {
          // Use appropriate API based on current user role
          const response = user?.role === 'SUPER_ADMIN' 
            ? await timesheetAPI.getSuperAdminUserTimesheets(userItem.id)
            : await timesheetAPI.getUserTimesheets(userItem.id);
          const userTimesheets = response?.data?.timesheets || response?.data || [];
          
          // Filter for DRAFT status and selected date
          let draftTimesheets = userTimesheets.filter(ts => {
            if (ts.status !== 'DRAFT') return false;
            
            // Check if the selected date falls within the timesheet date range
            const startDate = parseISO(ts.startDate);
            const endDate = parseISO(ts.endDate);
            
            return selectedDateObj >= startDate && selectedDateObj <= endDate;
          });
          
          // Filter by assigned projects if admin (not SUPER_ADMIN)
          // Only show drafts from projects the admin is assigned to
          if (assignedProjectIds !== null && assignedProjectIds.size > 0) {
            draftTimesheets = draftTimesheets.filter(ts => {
              const entries = ts.entries || [];
              // Check if any entry belongs to an assigned project
              return entries.some(entry => {
                const projectId = entry.project?.id || entry.projectId;
                return projectId && assignedProjectIds.has(projectId);
              });
            });
          } else if (assignedProjectIds !== null && assignedProjectIds.size === 0) {
            // If admin has no assigned projects, show no drafts
            draftTimesheets = [];
          }
          
          // Extract role name if it's an object
          const roleValue = typeof userItem.role === 'object' && userItem.role !== null 
            ? userItem.role.name 
            : userItem.role;
          
          return draftTimesheets.map(ts => ({
            ...ts,
            employee: userItem,
            employeeId: userItem.id,
            employeeName: `${userItem.firstName || ''} ${userItem.lastName || ''}`.trim(),
            employeeEmail: userItem.email,
            employeeRole: roleValue,
          }));
        } catch (error) {
          console.error(`Error fetching timesheets for user ${userItem.id}:`, error);
          return [];
        }
      });
      
      const results = await Promise.all(timesheetPromises);
      return results.flat();
    },
    {
      enabled: allUsers.length > 0 && (user?.role === 'SUPER_ADMIN' || assignedProjects !== undefined),
    }
  );

  // Group draft timesheets by project, then by manager, then by employee
  const groupedData = useMemo(() => {
    const grouped = {};
    
    allDraftTimesheets.forEach(timesheet => {
      // Get project name from timesheet entries or use "No Project"
      const entries = timesheet.entries || [];
      const projectNames = new Set();
      
      entries.forEach(entry => {
        // Handle different entry structures
        const projectName = entry.project?.name || 
                           entry.projectName || 
                           (typeof entry.project === 'string' ? entry.project : null);
        if (projectName) {
          projectNames.add(projectName);
        }
      });
      
      // If no project found, use "No Project"
      const projectName = projectNames.size > 0 
        ? Array.from(projectNames)[0] 
        : 'No Project';
      
      // Get manager name (assignedByManagerNames or employee's manager)
      const managerName = timesheet.assignedByManagerNames || 
                         timesheet.employee?.manager?.firstName && timesheet.employee?.manager?.lastName
                         ? `${timesheet.employee.manager.firstName} ${timesheet.employee.manager.lastName}`
                         : 'No Manager';
      
      if (!grouped[projectName]) {
        grouped[projectName] = {};
      }
      
      if (!grouped[projectName][managerName]) {
        grouped[projectName][managerName] = {};
      }
      
      if (!grouped[projectName][managerName][timesheet.employeeId]) {
        grouped[projectName][managerName][timesheet.employeeId] = {
          employee: timesheet.employee,
          employeeName: timesheet.employeeName,
          employeeEmail: timesheet.employeeEmail,
          employeeRole: timesheet.employeeRole,
          timesheets: [],
        };
      }
      
      grouped[projectName][managerName][timesheet.employeeId].timesheets.push(timesheet);
    });
    
    return grouped;
  }, [allDraftTimesheets]);

  // Filter grouped data based on search term
  const filteredGroupedData = useMemo(() => {
    if (!searchTerm.trim()) return groupedData;
    
    const searchLower = searchTerm.toLowerCase();
    const filtered = {};
    
    Object.keys(groupedData).forEach(projectName => {
      const projectMatches = projectName.toLowerCase().includes(searchLower);
      const projectData = groupedData[projectName];
      const filteredManagers = {};
      
      Object.keys(projectData).forEach(managerName => {
        const managerMatches = managerName.toLowerCase().includes(searchLower);
        const managerData = projectData[managerName];
        const filteredEmployees = {};
        
        Object.keys(managerData).forEach(employeeId => {
          const employee = managerData[employeeId];
          const employeeMatches = 
            employee.employeeName.toLowerCase().includes(searchLower) ||
            employee.employeeEmail.toLowerCase().includes(searchLower);
          
          if (projectMatches || managerMatches || employeeMatches) {
            filteredEmployees[employeeId] = employee;
          }
        });
        
        if (Object.keys(filteredEmployees).length > 0) {
          filteredManagers[managerName] = filteredEmployees;
        }
      });
      
      if (Object.keys(filteredManagers).length > 0) {
        filtered[projectName] = filteredManagers;
      }
    });
    
    return filtered;
  }, [groupedData, searchTerm]);

  const toggleProject = (projectName) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectName)) {
      newExpanded.delete(projectName);
    } else {
      newExpanded.add(projectName);
    }
    setExpandedProjects(newExpanded);
  };

  const toggleManager = (projectName, managerName) => {
    const key = `${projectName}-${managerName}`;
    const newExpanded = new Set(expandedManagers);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedManagers(newExpanded);
  };

  const getTotalDrafts = () => {
    return allDraftTimesheets.length;
  };

  const getTotalEmployees = () => {
    const employeeIds = new Set();
    allDraftTimesheets.forEach(ts => {
      if (ts.employeeId) employeeIds.add(ts.employeeId);
    });
    return employeeIds.size;
  };

  const isLoading = usersLoading || timesheetsLoading || projectsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex justify-between items-center"
      >
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Draft Timesheet Report</h1>
          <p className="text-gray-600 mt-1">View all draft timesheets organized by project, manager, and employee</p>
        </div>
      </motion.div>

      {/* Summary Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Draft Timesheets</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{getTotalDrafts()}</p>
            </div>
            <FileText className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Employees with Drafts</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{getTotalEmployees()}</p>
            </div>
            <Users className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Selected Date</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">
                {format(parseISO(selectedDate), 'MMM dd, yyyy')}
              </p>
            </div>
            <Calendar className="w-8 h-8 text-purple-500" />
          </div>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Date</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="form-input pl-10 text-gray-900"
              />
            </div>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by project, manager, or employee..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10 text-gray-900 placeholder:text-gray-400"
              />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Draft Timesheets Report */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-lg shadow-sm"
      >
        {Object.keys(filteredGroupedData).length > 0 ? (
          <div className="divide-y divide-gray-200">
            {Object.entries(filteredGroupedData).map(([projectName, managers]) => (
              <div key={projectName} className="p-6">
                {/* Project Header */}
                <div className="w-full flex items-center justify-between text-left hover:bg-gray-50 p-3 rounded-lg transition-colors">
                  <div className="flex items-center space-x-3">
                    <FolderOpen className="w-5 h-5 text-blue-500" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{projectName}</h3>
                      <p className="text-sm text-gray-500">
                        {Object.values(managers).reduce((sum, mgr) => 
                          sum + Object.values(mgr).reduce((empSum, emp) => empSum + emp.timesheets.length, 0), 0
                        )} draft timesheet(s)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const params = new URLSearchParams();
                        params.set('project', projectName);
                        params.set('date', selectedDate);
                        navigate(`/admin/draft-report/project?${params.toString()}`);
                      }}
                      className="px-3 py-1.5 text-xs font-medium rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100"
                      title="Open day view"
                    >
                      <Eye className="w-4 h-4 inline-block mr-1" />
                      View Day
                    </motion.button>
                    <button
                      onClick={() => toggleProject(projectName)}
                      className="p-2 rounded-md hover:bg-gray-100"
                      title={expandedProjects.has(projectName) ? 'Collapse' : 'Expand'}
                    >
                      {expandedProjects.has(projectName) ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Managers under Project */}
                {expandedProjects.has(projectName) && (
                  <div className="mt-4 ml-8 space-y-4">
                    {Object.entries(managers).map(([managerName, employees]) => (
                      <div key={managerName} className="border-l-2 border-gray-200 pl-4">
                        {/* Manager Header */}
                        <button
                          onClick={() => toggleManager(projectName, managerName)}
                          className="w-full flex items-center justify-between text-left hover:bg-gray-50 p-2 rounded-lg transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <User className="w-4 h-4 text-green-500" />
                            <div>
                              <h4 className="text-md font-medium text-gray-800">{managerName}</h4>
                              <p className="text-sm text-gray-500">
                                {Object.values(employees).reduce((sum, emp) => sum + emp.timesheets.length, 0)} draft(s)
                              </p>
                            </div>
                          </div>
                          {expandedManagers.has(`${projectName}-${managerName}`) ? (
                            <ChevronUp className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          )}
                        </button>

                        {/* Employees under Manager */}
                        {expandedManagers.has(`${projectName}-${managerName}`) && (
                          <div className="mt-2 ml-6 space-y-3">
                            {Object.values(employees).map((employeeData) => (
                              <div key={employeeData.employee.id} className="bg-gray-50 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <div>
                                    <h5 className="text-sm font-semibold text-gray-900">
                                      {employeeData.employeeName}
                                    </h5>
                                    <p className="text-xs text-gray-500">{employeeData.employeeEmail}</p>
                                    <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                                      {typeof employeeData.employeeRole === 'object' && employeeData.employeeRole !== null
                                        ? employeeData.employeeRole.name
                                        : employeeData.employeeRole || 'N/A'}
                                    </span>
                                  </div>
                                  <span className="text-xs text-gray-500">
                                    {employeeData.timesheets.length} draft(s)
                                  </span>
                                </div>
                                
                                {/* Timesheet List */}
                                <div className="space-y-2">
                                  {employeeData.timesheets.map((timesheet) => (
                                    <div
                                      key={timesheet.id}
                                      onClick={() => navigate(`/admin/timesheets/${timesheet.id}`)}
                                      className="flex items-center justify-between p-3 bg-white rounded border border-gray-200 hover:border-blue-300 hover:shadow-sm cursor-pointer transition-all"
                                    >
                                      <div className="flex items-center space-x-3">
                                        <FileText className="w-4 h-4 text-gray-400" />
                                        <div>
                                          <p className="text-sm font-medium text-gray-900">
                                            {format(parseISO(timesheet.startDate), 'MMM dd')} - {format(parseISO(timesheet.endDate), 'MMM dd, yyyy')}
                                          </p>
                                          <p className="text-xs text-gray-500">
                                            {timesheet.totalHours || 0} hours • ID: {timesheet.id}
                                          </p>
                                        </div>
                                      </div>
                                      <div className="flex items-center space-x-2">
                                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                                          DRAFT
                                        </span>
                                        <Eye className="w-4 h-4 text-gray-400 hover:text-blue-600" />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No draft timesheets found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {user?.role !== 'SUPER_ADMIN' && assignedProjectIds !== null && assignedProjectIds.size === 0
                ? 'You are not assigned to any projects. Please contact Super Admin to be assigned to projects.'
                : searchTerm 
                  ? 'Try adjusting your search criteria.'
                  : `No draft timesheets found for ${format(parseISO(selectedDate), 'MMM dd, yyyy')}.`
              }
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminDraftReport;
